import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Power, PowerOff, Trash2 } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { AiAssistantWidget } from '@/components/AiAssistantWidget'
import {
  CampaignConfigForm,
  type ApprovedTemplateOption,
  type PromptOption,
} from '@/components/CampaignConfigForm'
import { FollowupConfigSection } from '@/components/campaigns/FollowupConfigSection'
import { LeadNewConfigSection } from '@/components/campaigns/LeadNewConfigSection'
import { ReactivationConfigSection } from '@/components/campaigns/ReactivationConfigSection'
import { useAuthStore } from '@/stores/auth'
import type {
  AiAssistantPageContext,
  Campaign,
  CampaignConfig,
  CampaignResponse,
  CampaignType,
  PromptAvailableResponse,
  TemplateListResponse,
} from '@sdr-agend/admin-schemas'
import { CAMPAIGN_TYPES, DEFAULT_CAMPAIGN_CADENCE } from '@sdr-agend/admin-schemas'

const TYPE_LABELS: Record<CampaignType, string> = {
  confirmation: 'Confirmação',
  lead_new: 'Lead Novo',
  reactivation: 'Reativação',
}

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

interface FormState {
  name: string
  type: CampaignType
  config: CampaignConfig
}

const EMPTY_CONFIG: CampaignConfig = {
  cadence: DEFAULT_CAMPAIGN_CADENCE,
  templates: { c1: '', c2: '', c3: '' },
  business_hours_override: null,
  prompt_id: '',
}

const EMPTY_FORM: FormState = {
  name: '',
  type: 'confirmation',
  config: EMPTY_CONFIG,
}

/**
 * `/admin/campaigns/:id` and `/admin/campaigns/new`.
 *
 * The form drives both create and update; status (active/inactive) drives the
 * action buttons. Activate/Deactivate are admin-only and atomic on the server
 * (UNIQUE PARTIAL on `(type) WHERE active=TRUE`).
 *
 * Confirmation modal uses native `window.confirm` to avoid pulling Radix
 * Dialog into the bundle for a single yes/no flow. Replace with Dialog if
 * UX feedback requires richer copy.
 */
export function CampaignDetailPage(): React.ReactElement {
  const { id } = useParams<{ id?: string }>()
  const isNew = id === undefined || id === 'new'
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'admin'
  const canMutate = role === 'admin' || role === 'manager'

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const campaignPageContext: AiAssistantPageContext | null =
    !isNew && id !== undefined ? { page: 'campaign', campaignId: id } : null

  const { data: campaignData, isLoading } = useQuery<CampaignResponse>({
    queryKey: ['admin-campaigns', id],
    queryFn: async () => api<CampaignResponse>(`/admin/campaigns/${id ?? ''}`),
    enabled: !isNew,
  })

  // Templates dropdown: fetch APPROVED + active (qualquer idioma).
  const { data: templatesData } = useQuery<TemplateListResponse>({
    queryKey: ['admin-templates', { status: 'APPROVED' }],
    queryFn: async () => api<TemplateListResponse>('/admin/templates?status=APPROVED'),
  })

  const approvedTemplates: ApprovedTemplateOption[] = useMemo(() => {
    const rows = templatesData?.templates ?? []
    // Mostra qualquer template APPROVED + active, independentemente do idioma —
    // o operador escolhe (o label exibe o idioma para evitar engano).
    return rows
      .filter((t) => t.status === 'APPROVED' && t.active)
      .map((t) => ({ name: t.name, label: `${t.name} (${t.language})` }))
  }, [templatesData])

  // Story 3.19 — seletor de prompt: lista os prompts DISPONÍVEIS (versão ativa
  // de cada lineage NÃO atrelada a campanha ativa). O valor salvo em
  // `config.prompt_id` é o `lineageId`. Garantimos que o prompt atual da
  // campanha apareça como opção mesmo se já estiver em uso (campanha ativa).
  const { data: availableData } = useQuery<PromptAvailableResponse>({
    queryKey: ['admin-prompts', 'available'],
    queryFn: async () => api<PromptAvailableResponse>('/admin/prompts/available'),
  })

  const prompts: PromptOption[] = useMemo(() => {
    const current = campaignData?.campaign.config.prompt_id ?? ''
    const opts: PromptOption[] = (availableData?.available ?? [])
      .filter((p) => p.lineageId !== null)
      .map((p) => ({ id: p.lineageId as string, label: `${p.name ?? 'Prompt'} (v${p.version})` }))
    if (current !== '' && !opts.some((o) => o.id === current)) {
      opts.unshift({ id: current, label: `Prompt atual (${current.slice(0, 8)}…)` })
    }
    return opts
  }, [availableData, campaignData])

  const linkedPromptId: string | null = useMemo(() => {
    const current = campaignData?.campaign.config.prompt_id ?? ''
    return current === '' ? null : current
  }, [campaignData])

  useEffect(() => {
    if (campaignData?.campaign !== undefined) {
      const c = campaignData.campaign
      setForm({ name: c.name, type: c.type, config: c.config })
    }
  }, [campaignData])

  const campaign: Campaign | undefined = campaignData?.campaign

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  const createMutation = useMutation({
    mutationFn: async () =>
      api<CampaignResponse>('/admin/campaigns', {
        method: 'POST',
        body: JSON.stringify({ name: form.name, type: form.type, config: form.config }),
      }),
    onSuccess: (res) => {
      notify({ variant: 'default', title: 'Campaign criada', description: res.campaign.name })
      void queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })
      navigate(`/admin/campaigns/${res.campaign.id}`)
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao criar', description: formatError(err) })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async () =>
      api<CampaignResponse>(`/admin/campaigns/${id ?? ''}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: form.name, config: form.config }),
      }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Campaign atualizada' })
      void queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao atualizar', description: formatError(err) })
    },
  })

  const activateMutation = useMutation({
    mutationFn: async () =>
      api<CampaignResponse>(`/admin/campaigns/${id ?? ''}/activate`, { method: 'POST' }),
    onSuccess: () => {
      notify({
        variant: 'default',
        title: 'Campaign ativada',
        description: 'Demais campaigns deste tipo foram desativadas atomicamente.',
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao ativar', description: formatError(err) })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: async () =>
      api<CampaignResponse>(`/admin/campaigns/${id ?? ''}/deactivate`, { method: 'POST' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Campaign desativada' })
      void queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao desativar', description: formatError(err) })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api<{ campaign: Campaign }>(`/admin/campaigns/${id ?? ''}`, { method: 'DELETE' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Campaign removida' })
      void queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] })
      navigate('/admin/campaigns')
    },
    onError: (err: Error) => {
      notify({ variant: 'destructive', title: 'Falha ao remover', description: formatError(err) })
    },
  })

  function handleActivate(): void {
    const ok = window.confirm(
      `Ao ativar esta campaign, qualquer outra do tipo "${TYPE_LABELS[form.type]}" será desativada automaticamente. Confirmar?`,
    )
    if (ok) activateMutation.mutate()
  }

  function handleDelete(): void {
    const ok = window.confirm(`Remover a campaign "${form.name}"? (soft-delete)`)
    if (ok) deleteMutation.mutate()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label="Voltar">
            <Link to="/admin/campaigns">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? 'Nova Campaign' : (campaign?.name ?? '...')}
            </h1>
            {campaign !== undefined && (
              <div className="mt-1 flex items-center gap-2">
                <span className="rounded-md border px-2 py-0.5 text-xs uppercase tracking-wide">
                  {TYPE_LABELS[campaign.type]}
                </span>
                {campaign.active ? (
                  <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                    Ativa
                  </span>
                ) : (
                  <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    Inativa
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isNew && campaign !== undefined && !campaign.active && canMutate && (
            <Button type="button" onClick={handleActivate} disabled={activateMutation.isPending}>
              <Power className="h-4 w-4" aria-hidden="true" />
              Ativar
            </Button>
          )}
          {!isNew && campaign?.active === true && canMutate && (
            <Button
              type="button"
              variant="outline"
              onClick={() => deactivateMutation.mutate()}
              disabled={deactivateMutation.isPending}
            >
              <PowerOff className="h-4 w-4" aria-hidden="true" />
              Desativar
            </Button>
          )}
          {!isNew && isAdmin && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Remover
            </Button>
          )}
        </div>
      </div>

      <form
        className="grid gap-6"
        onSubmit={(e) => {
          e.preventDefault()
          if (isNew) createMutation.mutate()
          else updateMutation.mutate()
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor="name">Nome</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            disabled={!canMutate}
            required
            placeholder="ex: Confirmação Padrão"
            maxLength={200}
          />
        </div>

        <div className="grid gap-2 sm:max-w-sm">
          <Label htmlFor="type">Tipo</Label>
          <Select
            id="type"
            value={form.type}
            onChange={(e) => setForm((s) => ({ ...s, type: e.target.value as CampaignType }))}
            disabled={!isNew}
          >
            {CAMPAIGN_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
          {!isNew && (
            <p className="text-xs text-muted-foreground">
              O tipo é imutável após criação para preservar o vínculo com a UNIQUE partial.
            </p>
          )}
        </div>

        <CampaignConfigForm
          value={form.config}
          onChange={(next) => setForm((s) => ({ ...s, config: next }))}
          approvedTemplates={approvedTemplates}
          prompts={prompts}
          disabled={!canMutate}
          // Story 3.20 follow-up (user feedback 2026-07-15): lead_new never
          // read the legacy Cadência/Templates C1-C4 section at all — showing
          // it next to FollowupConfigSection below was confusing, not just
          // unused. Hide it here instead of leaving both on screen together.
          hideLegacyCadence={form.type === 'lead_new'}
        />

        {/* Story 5.8 — conditional config sections per campaign type. */}
        {form.type === 'lead_new' && (
          <>
            <LeadNewConfigSection
              value={form.config}
              onChange={(next) => setForm((s) => ({ ...s, config: next }))}
              prompts={prompts}
              approvedTemplates={approvedTemplates}
              disabled={!canMutate}
            />
            <FollowupConfigSection
              value={form.config}
              onChange={(next) => setForm((s) => ({ ...s, config: next }))}
              approvedTemplates={approvedTemplates}
              disabled={!canMutate}
            />
          </>
        )}
        {form.type === 'reactivation' && (
          <ReactivationConfigSection
            value={form.config}
            onChange={(next) => setForm((s) => ({ ...s, config: next }))}
            disabled={!canMutate}
          />
        )}

        {linkedPromptId !== null && (
          <div className="text-sm">
            <Link
              to={`/admin/prompts/${linkedPromptId}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              Editar prompt vinculado →
            </Link>
          </div>
        )}

        {canMutate && (
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending || isLoading}
            >
              {isNew ? 'Criar Campaign' : 'Salvar Alterações'}
            </Button>
          </div>
        )}
      </form>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>

      <AiAssistantWidget key={id ?? 'new'} pageContext={campaignPageContext} />
    </div>
  )
}

function formatError(err: Error): string {
  if (err instanceof ApiError) {
    if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
      const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
      return e.message ?? e.code ?? err.message
    }
  }
  return err.message
}

export default CampaignDetailPage
